// Compact metadata strip — sits between the page-level breadcrumb and the
// FilterToolbar / 4-column workspace. Christian's brief 2026-05-09:
//
//   * Asset is the most important field; show it FIRST + biggest.
//   * Site (= park) second.
//   * Drop "Type" (always "routine" for now) + "instructionDate" (the
//     review-date column the BE serves; we want the flight date instead).
//   * Keep Code (the user-friendly flight ID).
//   * Keep Flight date.
//   * Shrink the surrounding padding — the previous `p: 3` gave the
//     widget a balcony around three lines of text.
//
// State pill stays — it's the workflow-action affordance the analyst
// reaches for to verify the inspection isn't already locked / approved.
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { typography, compactControlHeight } from "@aeriosiq/ui";
import type {
  AssetSummary,
  DataAcquisitionDetail,
  SiteSummary,
} from "@aeriosiq/types";
import { DataAcquisitionStatusChip } from "../data-acquisitions/DataAcquisitionStatusChip.js";
import { formatAsset, formatSite } from "../data-acquisitions/enrichment.js";
import { ReviewProgressBar } from "./components/ReviewProgressBar.js";
import { WorkflowHeaderActions } from "./components/WorkflowHeaderActions.js";
import { CatalogueReadout } from "./components/CatalogueReadout.js";
import { UndoRedoCluster } from "./components/UndoRedoCluster.js";
import { AssetInfoBurgerMenu } from "./components/AssetInfoBurgerMenu.js";
import { FlightDateHint, FlightsSection } from "./FlightsSection.js";

interface Props {
  detail: DataAcquisitionDetail;
  site: SiteSummary | undefined;
  asset: AssetSummary | undefined;
  // Story 027 / 065 — workflow action context. Passed by the parent so
  // the header doesn't reach into auth.
  prescreenerUserId?: string | null;
  expertUserId?: string | null;
  meId?: string | null | undefined;
  meRoles?: readonly string[] | undefined;
}

function Field({
  label,
  children,
  emphasis = false,
}: {
  label: string;
  children: React.ReactNode;
  /** When true, render the value at h2-style weight + slightly larger
   *  text. Used for Asset (the dominant identifier on this page). */
  emphasis?: boolean;
}) {
  // UIOPT-01 — deterministic testids so the acceptance spec can read each
  // label's top edge (AC1) and compare value font sizes (AC4) without
  // brittle text matching.
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return (
    <Stack spacing={0.25}>
      <Typography
        component="div"
        data-testid={`header-field-label-${slug}`}
        sx={{
          fontSize: typography.size["label-md"],
          letterSpacing: typography.tracking["label-md"],
          color: "text.secondary",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Box
        data-testid={`header-field-value-${slug}`}
        sx={{
          // UIOPT-01 item 4 — every value (Asset included) renders at the
          // shared `body-md` size. `emphasis` now only carries WEIGHT, not
          // size: the Asset value stays semibold so it reads as the dominant
          // identifier by boldness, not by being larger than its siblings.
          fontSize: typography.size["body-md"],
          fontWeight: emphasis
            ? typography.weight.semibold
            : typography.weight.regular,
          color: "text.primary",
          lineHeight: 1.2,
          // UIOPT-01 (2026-06-09) — pin the value cell to the shared dense
          // control height and centre its content so the plain text values
          // line up on the same horizontal centre-line as the taller
          // catalogue/class-model dropdowns in the second row.
          minHeight: compactControlHeight,
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}

export function AnalysisHeader({
  detail,
  site,
  asset,
  prescreenerUserId = null,
  expertUserId = null,
  meId = null,
  meRoles,
}: Props) {
  // Use the page-level h2 here — the page-level h1 is gone (Christian's
  // brief). Screen readers still get a semantic heading per WCAG 2.4.6.
  return (
    <Paper
      variant="outlined"
      role="region"
      aria-labelledby="analysis-header-asset"
      sx={{ px: 1.5, py: 0.75 }}
    >
      <Stack spacing={0.5}>
        <Stack
          direction="row"
          spacing={3}
          useFlexGap
          flexWrap="wrap"
          // UIOPT-01 item 1 — top-align so every uppercase field label shares
          // one top baseline regardless of the value height beneath it. The
          // catalogue/class-model pickers render taller than the plain-text
          // values, so `center` would push their label off the shared baseline.
          // The right-hand control cluster re-centres itself (alignSelf below).
          alignItems="flex-start"
        >
          <Field label="Asset" emphasis>
            <Typography
              id="analysis-header-asset"
              component="h2"
              sx={{
                // UIOPT-01 item 4 — Asset name parity: `body-md` to match the
                // Site / DA Code / Flight-date value size, semibold kept so it
                // still reads as the dominant identifier. The h2 semantics
                // (aria-labelledby target, WCAG 2.4.6) are unchanged.
                fontSize: typography.size["body-md"],
                fontWeight: typography.weight.semibold,
                lineHeight: 1.2,
                color: "text.primary",
              }}
            >
              {formatAsset(asset, detail.assetId)}
            </Typography>
          </Field>
          <Field label="Site">{formatSite(site, detail.siteId)}</Field>
          {/* R58-L-01 — Code badge clarified. The previous "Code" label was
              ambiguous (which code?). Renamed to "Data Acquisition Code"
              with a tooltip mapping it to the wire column name.
              SB6-ANA-DA-01 / lyse@6.2.0 — Prefer the DA-level
              `flightCode` (data_acquisitions.flight_code, BIGINT) over
              `instructionCode` (parent work-order code). Fall back to
              instructionCode for back-compat with un-bumped servers. */}
          <Tooltip
            title="data_acquisitions.flight_code"
            arrow
            placement="bottom-start"
          >
            <span>
              <Field label="Data Acquisition Code">
                {detail.flightCode ?? detail.instructionCode}
              </Field>
            </span>
          </Tooltip>
          {detail.flightDate ? (
            <Field label="Flight date">
              {detail.flightDate.slice(0, 10)}
              {/* DI-3-5 — "first day of n" when the flights span several
                  days (Intake Workflows v2 §7; DI-D39). */}
              <FlightDateHint flights={detail.flights} />
            </Field>
          ) : null}
          {/* R58-L-01 (SB5 Track C.2) — Unified catalogue · class-model
              readout. Replaces the previous side-by-side
              EvaluationCataloguePicker + ClassModelPicker pair with one
              <catalog name> · <class model name> field. The class-model
              half is a dropdown until first defect lands, then a static
              chip. Catalogue-swap UI is out of scope this storybook
              (one catalogue per tenant by default; the picker widget
              stays in the codebase for future multi-catalogue tenants
              but is not rendered here). */}
          <CatalogueReadout
            dataAcquisitionId={detail.id}
            initialActiveCatalogueId={detail.activeCatalogueId ?? null}
            initialActiveClassModelId={detail.activeClassModelId ?? null}
            initialClassModelLockedAt={detail.classModelLockedAt ?? null}
          />
          <Box sx={{ flexGrow: 1 }} />
          {/* UIOPT-01 item 1 — the action cluster (chip + CTAs + undo/redo +
              burger) is grouped so it stays vertically centred against the
              top-aligned field block. `spacing={3}` + `useFlexGap` preserve the
              exact 24px gaps these controls had as direct row children. */}
          <Stack
            direction="row"
            spacing={3}
            useFlexGap
            alignItems="center"
            alignSelf="center"
            sx={{ flexShrink: 0 }}
          >
            {/* SB6-DA-01 — same per-state colour band as the Data
                Acquisitions list, so a dataset's colour is consistent
                between the list and the analysis surface. */}
            <DataAcquisitionStatusChip state={detail.state} />
            {/* Story 027 + 065 — primary workflow CTAs (Mark … Complete /
                Sign Off & Lock, Approve). The Reject action moved into the
                burger menu (SB6-ANA-HEADER-02). */}
            <WorkflowHeaderActions
              dataAcquisitionId={detail.id}
              evaluationState={detail.state}
              prescreenerUserId={prescreenerUserId}
              expertUserId={expertUserId}
              meId={meId}
              meRoles={meRoles}
            />
            {/* SB6-ANA-TILE-02 — Undo / Redo IconButton pair lifted from
                the Tiles widget header to the top-bar. Hotkeys are
                installed in AnalysisLayoutMount and scoped to the
                analysis app (the keydown handler bails on input/textarea
                focus and is unmounted with the feature). Stays visible. */}
            <UndoRedoCluster />
            {/* SB6-ANA-HEADER-02 — the Asset Info burger absorbs Reload all,
                Release (self-unassign), Reject, Download dataset, Pin
                dataset, Split view and Settings. Top-right corner, XS-scale
                overflow button. */}
            <AssetInfoBurgerMenu
              dataAcquisitionId={detail.id}
              evaluationState={detail.state}
              prescreenerUserId={prescreenerUserId}
              expertUserId={expertUserId}
              meId={meId}
              meRoles={meRoles}
            />
          </Stack>
        </Stack>
        {/* DI-3-5 — the Flights section: under the asset facts, above the
            widgets; beside the three-dots menu, never inside it. */}
        <FlightsSection
          flights={detail.flights}
          blobStateCounts={detail.blobStateCounts}
        />
        {/* Story 065 — per-user review progress.  Hidden when phase is
            not in {PRE_SCREENING, EXPERT_ANALYSIS, EVALUATION_DONE}. */}
        <ReviewProgressBar
          dataAcquisitionId={detail.id}
          evaluationState={detail.state}
        />
      </Stack>
    </Paper>
  );
}

export function AnalysisHeaderSkeleton() {
  return (
    <Paper
      variant="outlined"
      sx={{ px: 1.5, py: 0.75 }}
      aria-busy="true"
      aria-label="Loading inspection details"
    >
      <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap">
        {Array.from({ length: 4 }).map((_, i) => (
          <Stack key={i} spacing={0.25}>
            <Skeleton variant="text" width={48} height={12} />
            <Skeleton variant="text" width={120} height={20} />
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
